import type { ApolloError } from '@apollo/client';
import { createContext, useContext } from 'react';

export type CheckEmailAuthenticationMethod = (
  email: string,
  method: { beacon: boolean; singleSignOn: boolean },
) => Promise<boolean>;
export type AuthenticationMethodError = ApolloError | Error;

export type AuthenticationMethod = 'password' | 'sso' | 'beacon';
export type EmailAuthenticationMethod = Exclude<
  AuthenticationMethod,
  'password'
>;

type AuthenticationMethodContext = {
  /**
   * Email input value
   */
  email?: string;
  /**
   * `AuthenticationInfo` request error
   */
  error?: AuthenticationMethodError;
  /**
   * Whether current email is enabled for single sign-on
   */
  isSingleSignOnEnabled: boolean;
  /**
   * URL to redirect to for single sign-on flow
   */
  singleSignOnUrl: string | null;
  /**
   * Whether current email is enabled for Beacon Pro+
   */
  isBeaconEnabled: boolean;
  /**
   * URL to redirect to for Beacon Pro+ flow
   */
  beaconLoginUrl: string | null;
  /**
   * Loading state for `AuthenticationInfo` check
   */
  isLoading: boolean;
  /**
   * Which authentication method is the current active route
   * (e.g: `/login` vs. `/login/sso` vs. `/login/beacon`)
   */
  activeMethod: AuthenticationMethod;
  /**
   * Which authentication method is enabled for the current email
   */
  enabledMethod: AuthenticationMethod;
  /**
   * Update current email and make SSO-detection `AuthenticationInfo` request
   */
  checkEmail: CheckEmailAuthenticationMethod | null;
  /**
   * Redirect URL for current email if it is SSO-enabled
   */
  signInUrl?: string | null;
  /**
   * Final path the user will be redirected to after successful authentication flow
   */
  redirectPath?: string;
};

const defaultValue: AuthenticationMethodContext = {
  isSingleSignOnEnabled: false,
  isBeaconEnabled: false,
  singleSignOnUrl: null,
  beaconLoginUrl: null,
  isLoading: false,
  activeMethod: 'password',
  enabledMethod: 'password',
  checkEmail: null,
};

const context = createContext(defaultValue);
const { Provider } = context;
const useAuthenticationMethod = (): AuthenticationMethodContext =>
  useContext(context);

export {
  AuthenticationMethodContext,
  Provider,
  defaultValue,
  useAuthenticationMethod,
};
